import { type Theme } from "@mui/material/styles";

import { type ComponentOverrides } from "theme/theme.types";

import { focusRing } from "theme/theme.helpers";
import { TYPE_SCALE } from "theme/typography";

import { borderRadius } from "design-tokens";

const CHIP_SIZES = {
  medium: { px: 10, gap: 6, glyph: 16, type: TYPE_SCALE.sm },
  small: { px: 8, gap: 4, glyph: 14, type: TYPE_SCALE.xs },
} as const;

/** Every chip is stroked, filled included; the stroke is drawn inside the box, so the padding gives it back. */
const CHIP_BORDER = 1;

/**
 * Filled and outlined differ only at rest — `background.default` against `background.paper` behind
 * the same `grey[400]` stroke. Every other state paints both variants alike.
 */
const chipSurfaces = (theme: Theme, isOutlined: boolean) => ({
  default: {
    backgroundColor: isOutlined
      ? theme.palette.background.paper
      : theme.palette.background.default,
    borderColor: theme.palette.grey[400],
  },
  hover: { backgroundColor: theme.palette.grey[200], borderColor: theme.palette.grey[600] },
  pressed: {
    backgroundColor: theme.palette.background.default,
    borderColor: theme.palette.grey[600],
  },
  disabled: { backgroundColor: theme.palette.grey[100], borderColor: theme.palette.grey[200] },
});

const chipGlyphColor = (theme: Theme) => ({
  default: theme.palette.grey[400],
  active: theme.palette.grey[500],
  disabled: theme.palette.grey[300],
});

/** `.MuiChip-deletable` is here because MUI mints `.MuiChip-clickable` only when `onClick` is set. */
const CHIP_HOVER_SELECTOR = ["&.MuiChip-clickable:hover", "&.MuiChip-deletable:hover"].join(", ");

/**
 * Figma's `active` cell: pressing the chip drops the hover fill back to `background.default` and
 * darkens the X. Its `hovered-icon` cell paints the same, but reaching the chip from the glyph's
 * hover takes `:has()`, and nwsapi throws on that selector inside every `getComputedStyle` jsdom runs
 * — `user-event` calls one on each click — so a pointer on the X keeps the chip's hover fill and only
 * the X darkens. See `docs/known-visual-deltas.md`.
 */
const CHIP_PRESSED_SELECTOR = ["&.MuiChip-clickable:active", "&.MuiChip-deletable:active"].join(
  ", ",
);

const CHIP_INTERACTIVE_SELECTOR = `${CHIP_HOVER_SELECTOR}, ${CHIP_PRESSED_SELECTOR}`;

const CHIP_LABEL_STEP = {
  primary: "main",
  secondary: "main",
  error: "main",
  warning: "main",
  info: "dark",
  success: "dark",
} as const;

type ChipPaletteColor = keyof typeof CHIP_LABEL_STEP;

const CHIP_COLORS = Object.keys(CHIP_LABEL_STEP) as ChipPaletteColor[];

const chipColorPaint = (theme: Theme, color: ChipPaletteColor, isTinted: boolean) => ({
  backgroundColor: isTinted ? theme.palette[color].lighter : theme.palette.background.paper,
  borderColor: theme.palette[color].light,
  color: theme.palette[color][CHIP_LABEL_STEP[color]],
});

const chipColorVariants = CHIP_COLORS.flatMap((color) =>
  (["filled", "outlined"] as const).map((variant) => ({
    props: { color, variant },
    style: ({ theme }: { theme: Theme }) => {
      const paint = chipColorPaint(theme, color, variant === "filled");

      return {
        ...paint,
        [CHIP_INTERACTIVE_SELECTOR]: paint,
        "&.Mui-focusVisible": { ...paint, borderColor: theme.palette.primary.main },
      };
    },
  })),
);

export const MuiChip: ComponentOverrides["MuiChip"] = {
  styleOverrides: {
    root: ({ ownerState, theme }) => {
      const { px, gap, glyph, type } = CHIP_SIZES[ownerState.size === "small" ? "small" : "medium"];
      const surface = chipSurfaces(theme, ownerState.variant === "outlined");
      const glyphColor = chipGlyphColor(theme);
      const ring = theme.palette.primary.main;
      const paddingInline = px - CHIP_BORDER;

      return {
        ...type,
        // MUI chips are pills; the design draws a 4px corner at both sizes.
        borderRadius: borderRadius.DEFAULT,
        gap,
        paddingLeft: paddingInline,
        paddingRight: paddingInline,
        // MUI's base is `border: 0` and only its `outlined` rule replaces it, so `filled` needs the
        // stroke stated or a lone `borderColor` paints a zero-width line.
        borderWidth: CHIP_BORDER,
        borderStyle: "solid",
        color: theme.palette.text.primary,
        ...surface.default,

        // The box is stated in px as well as `fontSize`, because `font-size` reaches only MUI's
        // `SvgIcon`, which draws itself at `1em`. Everything in `assets/icons/` arrives through
        // `vite-plugin-svgr` as a raw `<svg>` carrying its own `width` / `height` attributes and
        // ignores it entirely; CSS out-ranks a presentation attribute, so stating the box sizes both
        // kinds. `fontSize` stays for em-based content inside a custom glyph, and because MUI hands
        // the leading icon a *larger* 18 at `size="small"` that would otherwise stand.
        // `inherit` rather than a colour of its own: Figma draws the leading icon in the label's
        // `text/primary`, where MUI paints `grey[700]`, and draws no disabled cell for it.
        "& .MuiChip-icon": {
          marginLeft: 0,
          marginRight: 0,
          fontSize: glyph,
          width: glyph,
          height: glyph,
          color: "inherit",
        },

        "& .MuiChip-deleteIcon": {
          marginLeft: 0,
          marginRight: 0,
          fontSize: glyph,
          width: glyph,
          height: glyph,
          color: glyphColor.default,
          // MUI writes this selector at the same specificity, one step lighter.
          "&:hover, &:active": { color: glyphColor.active },
        },

        // `boxShadow` is stated rather than simply left off: MUI lifts a clickable chip on `:active`
        // with `theme.shadows[1]`, and `theme/shadows.ts` pins rungs 1-8 to a real shadow, so
        // deleting the resting one alone would leave a chip that is flat until you press it. The X
        // keeps its resting grey here: Figma's `hovered` cell leaves it alone.
        [CHIP_HOVER_SELECTOR]: { ...surface.hover, boxShadow: "none" },

        // After the hover rule and at least as specific, so it wins while both match.
        [CHIP_PRESSED_SELECTOR]: {
          ...surface.pressed,
          boxShadow: "none",
          "& .MuiChip-deleteIcon": { color: glyphColor.active },
        },

        // Drawn outward so nothing reflows. Figma draws a 2px stroke inward against an unchanged
        // frame, which a border-box element cannot do without compensating the padding on a
        // transient state; the recoloured border plus a 1px ring is the same 2px, moved out. See
        // `docs/known-visual-deltas.md`. The hover rules carry a pseudo-class and out-rank this one,
        // so a chip under the pointer shows the pointer's paint whether or not it is focused.
        "&.Mui-focusVisible": {
          backgroundColor: surface.pressed.backgroundColor,
          borderColor: ring,
          boxShadow: focusRing(ring, CHIP_BORDER),
          "& .MuiChip-deleteIcon": { color: glyphColor.active },
        },

        // MUI fades disabled to `opacity: 0.38`; the design names three full-strength colours
        // instead. Colour-blind on purpose — Figma draws no disabled coloured cell, and this rule
        // carries a class the coloured variants' resting rules do not, so it wins on specificity.
        "&.Mui-disabled": {
          opacity: 1,
          color: theme.palette.text.disabled,
          ...surface.disabled,
          "& .MuiChip-deleteIcon": { color: glyphColor.disabled },
        },

        variants: chipColorVariants,
      };
    },
    label: { paddingLeft: 0, paddingRight: 0 },
  },
};
